import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { SpadeMark } from "./LogoLoader";

/**
 * Renders a single blog content block.
 * Block types: paragraph | heading | image | video | pullquote | divider |
 *              internal-link | cta
 */

// Inline markdown-style: **bold** and [link](url)
function inlineFormat(text) {
  if (!text) return "";
  // Split by **bold** and [link](url) markers, preserving them
  const parts = [];
  const re = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  for (const m of text.matchAll(re)) {
    if (m.index > last) parts.push({ t: "txt", v: text.slice(last, m.index) });
    const tok = m[0];
    if (tok.startsWith("**")) parts.push({ t: "b", v: tok.slice(2, -2) });
    else {
      const lm = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      parts.push({ t: "a", v: lm[1], h: lm[2] });
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push({ t: "txt", v: text.slice(last) });
  return parts.map((p, i) => {
    if (p.t === "b") return <strong key={i} className="text-white font-medium">{p.v}</strong>;
    if (p.t === "a") {
      const internal = p.h.startsWith("/");
      return internal ? (
        <Link key={i} to={p.h} className="text-[#C9A050] underline decoration-[#C9A050]/40 underline-offset-4 hover:decoration-[#C9A050]">{p.v}</Link>
      ) : (
        <a key={i} href={p.h} target="_blank" rel="noreferrer" className="text-[#C9A050] underline decoration-[#C9A050]/40 underline-offset-4 hover:decoration-[#C9A050]">{p.v}</a>
      );
    }
    return <span key={i}>{p.v}</span>;
  });
}

function vimeoEmbed(url) {
  if (!url) return "";
  const m = String(url).match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([\w]+))?/);
  if (!m) return url;
  const id = m[1];
  const h = m[2] ? `?h=${m[2]}` : "";
  return `https://player.vimeo.com/video/${id}${h}`;
}

export default function BlogBlock({ block }) {
  if (!block) return null;
  const t = block.type;

  if (t === "paragraph") {
    return (
      <p data-testid="blog-block-paragraph" className="text-[#D4D4D8] text-lg md:text-xl leading-[1.75] max-w-3xl whitespace-pre-line">
        {inlineFormat(block.text)}
      </p>
    );
  }

  if (t === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    const cls = block.level === 3
      ? "font-display text-2xl md:text-3xl text-white tracking-tight max-w-3xl mt-4"
      : "font-display text-3xl md:text-4xl lg:text-5xl text-white tracking-tight leading-tight max-w-3xl mt-6";
    return <Tag data-testid="blog-block-heading" className={cls}>{block.text}</Tag>;
  }

  if (t === "image" && block.src) {
    return (
      <figure data-testid="blog-block-image" className="my-2 -mx-6 md:mx-0">
        <img src={block.src} alt={block.alt || ""} className="w-full h-auto object-cover" />
        {block.caption && (
          <figcaption className="text-[#8A8A93] text-sm italic mt-4 px-6 md:px-0">{block.caption}</figcaption>
        )}
      </figure>
    );
  }

  if (t === "video" && block.src) {
    const embed = vimeoEmbed(block.src);
    return (
      <div data-testid="blog-block-video" className="my-2 -mx-6 md:mx-0">
        <BlogVideoFrame embed={embed} title={block.caption || "video"} />
        {block.caption && (
          <p className="text-[#8A8A93] text-sm italic mt-4 px-6 md:px-0">{block.caption}</p>
        )}
      </div>
    );
  }

  if (t === "pullquote") {
    return (
      <blockquote data-testid="blog-block-pullquote" className="my-8 md:my-12 max-w-3xl border-l-2 border-[#C9A050]/60 pl-6 md:pl-10">
        <p className="font-serif-accent italic text-[#E4E4E7] text-2xl md:text-3xl lg:text-4xl leading-[1.4] whitespace-pre-line">
          {block.text}
        </p>
      </blockquote>
    );
  }

  if (t === "divider") {
    return (
      <div data-testid="blog-block-divider" className="my-12 md:my-16 max-w-3xl">
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9A050]/30 to-transparent" />
      </div>
    );
  }

  if (t === "internal-link") {
    const internal = (block.href || "").startsWith("/");
    const Tag = internal ? Link : "a";
    const props = internal ? { to: block.href } : { href: block.href, target: "_blank", rel: "noreferrer" };
    return (
      <Tag
        {...props}
        data-testid="blog-block-internal-link"
        className="block my-4 max-w-3xl border border-white/10 bg-white/[0.02] rounded-xl p-6 md:p-7 hover:border-[#C9A050]/40 transition-colors group"
      >
        {block.label && <p className="overline text-[#C9A050]/70 mb-2">{block.label}</p>}
        <p className="font-display text-xl md:text-2xl text-white group-hover:text-[#C9A050] transition-colors">
          {block.text} <span className="text-[#C9A050]">→</span>
        </p>
      </Tag>
    );
  }

  if (t === "cta") {
    const internal = (block.href || "/contact").startsWith("/");
    const Tag = internal ? Link : "a";
    const props = internal ? { to: block.href || "/contact" } : { href: block.href, target: "_blank", rel: "noreferrer" };
    return (
      <div data-testid="blog-block-cta" className="my-12 md:my-16 max-w-3xl text-center md:text-left">
        {block.text && (
          <p className="font-serif-accent italic text-[#E4E4E7] text-2xl md:text-3xl mb-8 leading-relaxed">
            {block.text}
          </p>
        )}
        <Tag
          {...props}
          className="inline-block overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-7 py-4 transition-all"
        >
          {block.label || "Start a project →"}
        </Tag>
      </div>
    );
  }

  return null;
}

/* Premium video frame for blog video blocks — clean black + minimal gold
   spinner until the Vimeo player signals ready, then fades the iframe in.
   No thumbnail flash. Mirrors VimeoPlayer's premium mode. */
function BlogVideoFrame({ embed, title }) {
  const [loaded, setLoaded] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    setLoaded(false);
    const onMessage = (e) => {
      try {
        if (!e.origin || !e.origin.includes("vimeo.com")) return;
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        const evt = data?.event || data?.method;
        if (evt === "ready" || evt === "play" || evt === "playing" || evt === "loaded") {
          setLoaded(true);
        }
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("message", onMessage);
    // safety timeout — fade iframe in even if Vimeo never posts ready
    const t = setTimeout(() => setLoaded(true), 4000);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(t);
    };
  }, [embed]);

  return (
    <div
      data-testid="blog-video-frame"
      className="relative w-full bg-black overflow-hidden"
      style={{ paddingTop: "56.25%" }}
    >
      {!loaded && (
        <div
          aria-hidden="true"
          data-testid="blog-video-loading"
          className="absolute inset-0 flex items-center justify-center z-[1]"
        >
          <SpadeMark size="compact" />
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={embed}
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        title={title}
        style={{
          opacity: loaded ? 1 : 0,
          transition: "opacity 700ms ease",
        }}
      />
    </div>
  );
}
